import { useEffect, useRef, useState } from "react";
import Button from "../../component/Button";
import Checkbox from "../../component/CheckBox";

import { AxiosInstance } from "../../@api/api";
import { useNavigate } from "react-router-dom";
import { useResponsiveMobile } from "../../hooks/useResponsiveMobile";
import { FloatingInput } from "../../component/FloatingInput";
import { useModalStore } from "../../store/modalStore";
import { setDescription } from "../../seo/seo";

const SuccessModal = ({ onClose }: { onClose: () => void }) => {
  const isMobile = useResponsiveMobile(900);
  const navigate = useNavigate();
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className="
          flex flex-col gap-8
          relative z-10 w-full max-w-[300px] md:max-w-[640px]
          rounded-2xl bg-white
          px-8 pt-10 pb-[60px]
          shadow-[0_20px_60px_rgba(0,0,0,0.25)]
          text-center
        "
      >
        <button
          onClick={onClose}
          className="self-end text-[#696E77] text-xl hover:text-[#111]"
          aria-label="닫기"
        >
          ✕
        </button>

        <h2 className="md:text-[28px] text-[24px] font-normal text-[#121319]">
          문의가 정상적으로 {isMobile ? <br /> : ""} 접수되었습니다.
        </h2>

        <p className="text-[16px] text-[#4D4F58] leading-relaxed">
          고객님의 소중한 문의가 {isMobile ? <br /> : ""} 정상적으로 접수되었습니다.
          <br />
          담당자가 확인 후 최대한 {isMobile ? <br /> : ""}빠른 시간 내에 답변드리겠습니다.
        </p>
        <Button
          className="md:w-auto w-full min-w-[200px] md:min-w-[260px]"
          onClick={() => {
            onClose();
            navigate("/");
          }}
        >
          확인
        </Button>
      </div>
    </div>
  );
};

type Errors = {
  name?: boolean;
  phone?: boolean;
  email?: boolean;
  company?: boolean;
  industry?: boolean;
  agree?: boolean;
};

const trim = (v: string) => v.trim();

//  추가: 이메일/휴대폰 검증 유틸
const onlyDigits = (v: string) => v.replace(/\D/g, "");

// 너무 엄격하지 않게 “일반적인 이메일” 형태만 체크
const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);

// 한국 휴대폰(대표적으로 010/011/016/017/018/019) + 자리수 체크
// - 010: 11자리 (010xxxxxxxx)
// - 011~019: 10~11자리 케이스 존재
const isValidMobile = (digits: string) => /^(01[016789])\d{7,8}$/.test(digits);
//  보기 좋게 하이픈 포맷 (010-1234-5678 / 011-123-4567)

const Inquiry = () => {
  const openModal = useModalStore((s) => s.open);
  const agreeRef = useRef<HTMLInputElement | null>(null);
  const [onModal, setOnModal] = useState(false);
  const [agree, setAgree] = useState(false);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [industry, setIndustry] = useState("");
  const [content, setContent] = useState("");

  const [errors, setErrors] = useState<Errors>({});

  const nameRef = useRef<HTMLInputElement | null>(null);
  const phoneRef = useRef<HTMLInputElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const companyRef = useRef<HTMLInputElement | null>(null);
  const industryRef = useRef<HTMLInputElement | null>(null);

  const baseInput =
    "flex-1 border-[1.23px] border-[#C1C7CD] rounded-[9px] py-3 px-[14px] focus:outline-none focus:ring-0 focus:border-[#0079FF]";

  const errorInput = "border-[#FF3B30] focus:border-[#FF3B30]";

  const clearError = (key: keyof Errors) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateAndFocus = () => {
    const nextErrors: Errors = {};

    const tName = trim(name);
    const tEmail = trim(email);
    const phoneDigits = onlyDigits(phone);

    if (!tName) nextErrors.name = true;

    //  phone: 빈값 + 형식검증
    if (!phoneDigits) {
      nextErrors.phone = true;
    } else if (!isValidMobile(phoneDigits)) {
      nextErrors.phone = true;
    }

    //  email: 빈값 + 형식검증
    if (!tEmail) {
      nextErrors.email = true;
    } else if (!isValidEmail(tEmail)) {
      nextErrors.email = true;
    }

    if (!trim(company)) nextErrors.company = true;
    if (!trim(industry)) nextErrors.industry = true;
    if (!agree) nextErrors.agree = true;

    setErrors(nextErrors);

    if (nextErrors.name) return (nameRef.current?.focus(), false);
    if (nextErrors.phone) return (phoneRef.current?.focus(), false);
    if (nextErrors.email) return (emailRef.current?.focus(), false);
    if (nextErrors.company) return (companyRef.current?.focus(), false);
    if (nextErrors.industry) return (industryRef.current?.focus(), false);

    // ⚠️ agreeRef는 실제 input ref로 연결되어 있지 않으면 focus가 안 먹습니다.
    // (Checkbox 컴포넌트가 forwardRef 지원해야 함)
    if (nextErrors.agree) return false;

    return true;
  };

  const submitInquiry = async () => {
    try {
      const result = await AxiosInstance.post("/api/v1/createQna", {
        name: trim(name),
        telNo: onlyDigits(phone), //  서버에는 숫자만
        email: trim(email),
        body: content,
        termsYn: agree ? 1 : 0,
        bsKind: industry,
        coNm: company,
      });
    } catch (error) {
      console.error("문의하기 API 에러");
    }
  };

  const onSubmit = () => {
    const ok = validateAndFocus();
    if (!ok) return;

    submitInquiry();

    setName("");
    setIndustry("");
    setCompany("");
    setEmail("");
    setPhone("");
    setContent("");
    setOnModal(true);
    setAgree(false);
  };
  const isMobile = useResponsiveMobile(900);

  useEffect(() => {
    document.title = "품 : Poom | 고객센터";
    setDescription(
      "비즈니스의 원활한 운영과 성장을 위해 품(POOM)이 함께합니다. 서비스 이용 중 문의사항은 언제든 고객센터를 통해 해결하실 수 있습니다.",
    );
  }, []);

  return (
    <div className="w-full flex flex-col md:pb-[160px] pb-[120px] gap-[80px] md:gap-[160px]">
      {onModal && <SuccessModal onClose={() => setOnModal(false)} />}

      <section className="w-full flex flex-col pt-6 md:pt-3 justify-center items-center px-6">
        <div
          className="
            w-full md:max-w-[1600px]
            rounded-[24px]
            aspect-[16/5] md:aspect-[16/3.6]
            min-h-[170px] max-h-[360px]
            flex justify-center items-center
            overflow-hidden
          "
          style={{
            backgroundImage: isMobile
              ? "url('/assets/images/faq/bg_mobile.png')"
              : "url('/assets/images/faq/bg.png')",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "top center",
          }}
        >
          <span className="text-[#000] font-bold text-[20px] md:text-display3 text-center">
            고객센터
          </span>
        </div>
      </section>

      <section className="flex justify-center items-center px-6 md:px-[100px]">
        <div className="flex flex-col gap-12 md:gap-0 md:flex-row justify-between w-full max-w-[1440px]">
          <span className="font-bold text-[36px] md:text-display md:w-1/2 text-center md:text-left">
            품 솔루션과 함께할 <br />
            파트너를 기다립니다.
          </span>

          <div className="flex flex-col gap-10 md:gap-[100px] md:w-1/2">
            <div className="flex flex-col gap-4 md:gap-8">
              <div className="flex flex-col md:flex-row md:gap-3 gap-4">
                <FloatingInput
                  limitLength
                  label="성함"
                  value={name}
                  inputRef={nameRef}
                  error={errors.name}
                  onChange={(v) => {
                    setName(v);
                    clearError("name");
                  }}
                />

                <FloatingInput
                  limitLength
                  label="연락처"
                  value={phone}
                  inputRef={phoneRef}
                  error={errors.phone}
                  type="tel"
                  inputMode="numeric"
                  onChange={(v) => {
                    setPhone(onlyDigits(v));
                    clearError("phone");
                  }}
                />
              </div>

              <FloatingInput
                label="이메일"
                value={email}
                inputRef={emailRef}
                error={errors.email}
                type="email"
                inputMode="email"
                autoComplete="email"
                onChange={(v) => {
                  setEmail(v);
                  clearError("email");
                }}
              />

              <div className="relative w-full">
                <div
                  className={`
                    w-full min-h-[300px]
                    rounded-lg border border-[#C1C7CD]
                    bg-white
                    px-4 py-4
                    text-[16px] text-[#111]
                    outline-none resize-none
                  `}
                >
                  <div className="flex flex-col gap-2">
                    <div className="text-[16px] text-[#4D4F58]">
                      아래 항목을 입력 후 문의하기 버튼을 눌러주세요.
                    </div>

                    <ul className="flex flex-col gap-2 text-[#4D4F58]">
                      <li className="flex flex-row  items-center h-9">
                        <label htmlFor="company" className="text-base text-nowrap ">
                          •🏢 회사명 :
                        </label>
                        <input
                          id="company"
                          ref={companyRef}
                          value={company}
                          onChange={(e) => {
                            setCompany(e.target.value);
                            clearError("company");
                          }}
                          className={`
                            min-w-0
                            text-[18px]
                            rounded-[6px] px-2 py-1
                            border border-transparent
                            focus:outline-none focus:ring-0
                            ${errors.company ? "border-[#FF3B30]" : "border-transparent"}
                          `}
                        />
                      </li>

                      <li className="flex gap-2 items-center h-9">
                        <label htmlFor="industry" className="text-base text-nowrap">
                          •🏷️ 회사 업종 :
                        </label>
                        <input
                          id="industry"
                          ref={industryRef}
                          value={industry}
                          onChange={(e) => {
                            setIndustry(e.target.value);
                            clearError("industry");
                          }}
                          className={`
                            min-w-0
                            text-[18px]
                            flex-1 rounded-[6px] px-2 py-1
                            border border-transparent
                            focus:outline-none focus:ring-0
                            ${errors.industry ? "border-[#FF3B30]" : "border-transparent"}
                          `}
                        />
                      </li>

                      <div className="flex flex-col gap-2">
                        <label
                          htmlFor="inquiry_content"
                          className="flex gap-2 items-center w-full cursor-text h-9"
                        >
                          <span className="text-base">•📝 문의 내용 :</span>
                          <span className="flex-1 h-6" />
                        </label>
                        <textarea
                          id="inquiry_content"
                          value={content}
                          onChange={(e) => setContent(e.target.value)}
                          className="text-base min-h-[100px] focus:outline-none focus:ring-0 rounded-[6px] px-2 py-2 border border-transparent"
                          placeholder="방문 이유 / 궁금한 기능 / 비즈니스 상황 (자유 입력) 상황을 입력해주세요."
                        />
                      </div>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-6 md:gap-8 w-full">
              <div className="flex flex-row justify-between w-full">
                <div
                  className={`flex flex-row gap-2 items-center
                  ${errors.agree ? "ring-2 ring-[#FF3B30]" : ""}`}
                >
                  <Checkbox
                    label="(필수) 개인정보 수집 및 이용안내"
                    checked={agree}
                    onCheckedChange={() => setAgree(!agree)}
                  />
                </div>
                <button
                  onClick={() => {
                    openModal("terms");
                  }}
                  className={`font-normal text-[14px] md:text-base  text-[#0079FF] cursor-pointer flex items-center`}
                >
                  내용보기
                </button>
              </div>

              <Button
                onClick={onSubmit}
                className="bg-[#000] md:h-20 h-[48px] md:text-[20px] text-[16px] rounded-full"
                size="lg"
              >
                문의하기
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Inquiry;
